import { FormEvent, useEffect, useMemo, useState } from "react";
import { ArrowRight, Download, Landmark, LockKeyhole, Menu, Moon, RefreshCcw, Search, ShieldCheck, Sun } from "lucide-react";
import { demoBootstrap, fetchAdminLedger, fetchBootstrap, fetchPublicSummary, fetchReport, lookupMemberSummary } from "./api";
import { formatKes } from "./shared/money";
import { purposeLabel } from "./shared/purpose";
import type { AdminLedger, BootstrapData, Contribution, MemberSummary, ReportPeriod, ReportResult } from "./shared/types";

type Theme = "light" | "dark";

const projects = [
  {
    name: "Eastlands Community Outreach",
    description: "Food support, school supplies, and pastoral care for families across the estate.",
    raised: "720000.00",
    target: "1000000.00",
    progress: 72,
    image: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=900&q=80"
  },
  {
    name: "Sanctuary Renovation",
    description: "A safer, more welcoming worship space for Sunday services and weekday ministry.",
    raised: "4250000.00",
    target: "8000000.00",
    progress: 53,
    image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=900&q=80"
  },
  {
    name: "Youth Discipleship Fund",
    description: "Mentorship, camp scholarships, and creative arts programs for young people.",
    raised: "612400.00",
    target: "1000000.00",
    progress: 61,
    image: "https://images.unsplash.com/photo-1497486751825-1233686d5d80?auto=format&fit=crop&w=900&q=80"
  }
];

export function App() {
  const [theme, setTheme] = useState<Theme>(() => (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"));
  const [bootstrap, setBootstrap] = useState<BootstrapData>(demoBootstrap);
  const isAdmin = window.location.pathname.startsWith("/admin");

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    void fetchBootstrap().then(setBootstrap);
  }, []);

  return (
    <main className={isAdmin ? "app-shell admin-shell" : "public-shell"}>
      <SiteHeader isAdmin={isAdmin} theme={theme} onTheme={() => setTheme(theme === "dark" ? "light" : "dark")} />
      {isAdmin ? <AdminConsole bootstrap={bootstrap} /> : <PublicLanding />}
    </main>
  );
}

function SiteHeader({ isAdmin, theme, onTheme }: { isAdmin: boolean; theme: Theme; onTheme: () => void }) {
  return (
    <header className="site-header">
      <a className="brand" href="/">
        <Landmark size={28} />
        <span>
          Hope Chapel Nairobi
          <small>Love God. Love people. Serve Nairobi.</small>
        </span>
      </a>
      <nav className="site-nav" aria-label="Primary">
        {!isAdmin ? (
          <>
            <a href="#projects">Projects</a>
            <a href="#summary">Giving</a>
            <a href="#records">My Records</a>
            <a href="#latest">Latest</a>
          </>
        ) : (
          <a href="/">Public site</a>
        )}
        <button className="icon-button" onClick={onTheme} type="button">
          {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          <span className="sr-only">Toggle theme</span>
        </button>
        <button className="menu-button" type="button">
          <Menu size={20} />
          <span className="sr-only">Menu</span>
        </button>
      </nav>
    </header>
  );
}

function PublicLanding() {
  const [ledger, setLedger] = useState<AdminLedger | null>(null);
  const [lookup, setLookup] = useState<MemberSummary | null>(null);
  const [lookupError, setLookupError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void fetchPublicSummary().then(setLedger);
    const interval = window.setInterval(() => {
      void fetchPublicSummary().then(setLedger);
    }, 20000);
    return () => window.clearInterval(interval);
  }, []);

  async function handleLookup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const phone = String(form.get("phone") ?? "");
    const receiptCode = String(form.get("receiptCode") ?? "");
    setBusy(true);
    setLookupError("");
    try {
      setLookup(await lookupMemberSummary(phone, receiptCode));
    } catch (error) {
      setLookup(null);
      setLookupError(error instanceof Error ? error.message : "No matching contribution record.");
    } finally {
      setBusy(false);
    }
  }

  const summary = ledger ?? {
    totals: { today: "156820.00", monthToDate: "3842650.00", yearToDate: "15842650.00", lifetime: "28450120.00" },
    byPurpose: [],
    contributions: [],
    unclassifiedCount: 0,
    generatedAt: new Date().toISOString()
  };

  return (
    <>
      <section className="hero-section">
        <div className="hero-copy">
          <p className="hero-kicker">Together in stewardship</p>
          <h1>Together we fund the work</h1>
          <p>Your giving changes lives, supports ministry, and builds a stronger Nairobi community.</p>
          <div className="hero-actions">
            <a className="primary-button" href="#projects">
              Give toward a project
              <ArrowRight size={17} />
            </a>
            <a className="secondary-button glass" href="#records">
              Find my records
            </a>
          </div>
        </div>
        <div className="hero-progress">
          <strong>Eastlands Community Outreach</strong>
          <span>72% funded</span>
          <div className="progress-track">
            <i style={{ width: "72%" }} />
          </div>
          <small>{formatKes("720000.00")} raised of {formatKes("1000000.00")} goal</small>
        </div>
      </section>

      <section className="impact-strip" id="summary" aria-label="Contribution summary">
        <div>
          <span>Today</span>
          <strong>{formatKes(summary.totals.today)}</strong>
        </div>
        <div>
          <span>This month</span>
          <strong>{formatKes(summary.totals.monthToDate)}</strong>
        </div>
        <div>
          <span>This year</span>
          <strong>{formatKes(summary.totals.yearToDate)}</strong>
        </div>
        <div>
          <span>All time</span>
          <strong>{formatKes(summary.totals.lifetime)}</strong>
        </div>
      </section>

      <section className="section-block" id="projects">
        <div className="section-title">
          <div>
            <p className="eyebrow">Current Projects</p>
            <h2>See where every gift goes</h2>
          </div>
          <a href="#records">View records</a>
        </div>
        <div className="project-grid">
          {projects.map((project) => (
            <article className="project-card" key={project.name}>
              <img alt="" src={project.image} />
              <div>
                <strong>{project.name}</strong>
                <p>{project.description}</p>
                <div className="progress-track">
                  <i style={{ width: `${project.progress}%` }} />
                </div>
                <small>
                  {formatKes(project.raised)} of {formatKes(project.target)}
                </small>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="lookup-band" id="records">
        <div>
          <LockKeyhole size={28} />
          <h2>Find my records</h2>
          <p>Enter the phone number used for payment and a receipt code from one of your contributions.</p>
        </div>
        <form className="lookup-form" onSubmit={handleLookup}>
          <label>
            Phone
            <input name="phone" placeholder="e.g. 0700 111 001" required />
          </label>
          <label>
            Receipt code
            <input name="receiptCode" placeholder="e.g. SEED001" required />
          </label>
          <button className="primary-button" disabled={busy} type="submit">
            <Search size={17} />
            {busy ? "Checking" : "Search"}
          </button>
        </form>
        {lookupError ? <p className="form-error">{lookupError}</p> : null}
      </section>

      {lookup ? <MemberRecords summary={lookup} /> : null}

      <ContributionTable title="Latest Contributions" contributions={summary.contributions} publicView />

      <footer className="site-footer">
        <span>Secure giving. Contribution records are protected.</span>
        <a href="/admin">Church leadership and admin access</a>
      </footer>
    </>
  );
}

function MemberRecords({ summary }: { summary: MemberSummary }) {
  const [purpose, setPurpose] = useState("all");
  const filtered = useMemo(() => {
    return purpose === "all" ? summary.contributions : summary.contributions.filter((item) => item.purpose === purpose);
  }, [summary.contributions, purpose]);

  return (
    <section className="member-records">
      <div className="section-title">
        <div>
          <p className="eyebrow">Verified records</p>
          <h2>{summary.congregant.name}</h2>
        </div>
        <strong>{formatKes(summary.totals.lifetime)}</strong>
      </div>
      <div className="purpose-tabs">
        <button className={purpose === "all" ? "active" : ""} onClick={() => setPurpose("all")} type="button">
          All
        </button>
        {summary.byPurpose.map((item) => (
          <button className={purpose === item.purpose ? "active" : ""} key={item.purpose} onClick={() => setPurpose(item.purpose)} type="button">
            {purposeLabel(item.purpose)}
          </button>
        ))}
      </div>
      <ContributionTable title="Contribution History" contributions={filtered} />
    </section>
  );
}

function AdminConsole({ bootstrap }: { bootstrap: BootstrapData }) {
  const [ledger, setLedger] = useState<AdminLedger | null>(null);
  const [period, setPeriod] = useState<ReportPeriod>("daily");
  const [congregantId, setCongregantId] = useState("");
  const [report, setReport] = useState<ReportResult | null>(null);

  async function refresh() {
    setLedger(await fetchAdminLedger());
  }

  useEffect(() => {
    void fetchAdminLedger().then(setLedger);
    const interval = window.setInterval(() => {
      void fetchAdminLedger().then(setLedger);
    }, 15000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    void fetchReport(period, congregantId || undefined).then(setReport);
  }, [period, congregantId]);

  if (!ledger) return <LoadingState label="Loading treasury console" />;

  return (
    <section className="admin-page">
      <div className="section-title">
        <div>
          <p className="eyebrow">Treasury Console</p>
          <h1>Contribution Ledger</h1>
        </div>
        <div className="admin-actions">
          <span className="access-pill">
            <ShieldCheck size={16} />
            Access protected
          </span>
          <button className="secondary-button" onClick={refresh} type="button">
            <RefreshCcw size={16} />
            Refresh
          </button>
        </div>
      </div>
      <section className="admin-summary">
        <Metric label="Today" value={ledger.totals.today} />
        <Metric label="Month to date" value={ledger.totals.monthToDate} />
        <Metric label="Unclassified" value={String(ledger.unclassifiedCount)} plain />
      </section>
      <section className="admin-grid">
        <ContributionTable title="Realtime Contributions" contributions={ledger.contributions} />
        <aside className="report-panel">
          <h2>Reports</h2>
          <div className="segmented">
            {(["daily", "weekly", "monthly", "congregant"] as ReportPeriod[]).map((item) => (
              <button className={period === item ? "active" : ""} key={item} onClick={() => setPeriod(item)} type="button">
                {item}
              </button>
            ))}
          </div>
          {period === "congregant" ? (
            <label className="field-label">
              Congregant
              <select value={congregantId} onChange={(event) => setCongregantId(event.target.value)}>
                <option value="">All congregants</option>
                {bootstrap.congregants.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {report ? (
            <div className="report-summary">
              <strong>{formatKes(report.total)}</strong>
              <span>{report.rows.length} rows generated</span>
              <a className="primary-button" href={`/api/reports/export.csv?period=${period}${congregantId ? `&congregantId=${congregantId}` : ""}`}>
                <Download size={16} />
                Export CSV
              </a>
            </div>
          ) : null}
        </aside>
      </section>
    </section>
  );
}

function ContributionTable({ title, contributions, publicView = false }: { title: string; contributions: Contribution[]; publicView?: boolean }) {
  return (
    <section className="table-section" id="latest">
      <div className="section-title compact">
        <h2>{title}</h2>
        <span>{contributions.length} records</span>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>{publicView ? "Contributor" : "Name"}</th>
              <th>Purpose</th>
              <th>Method</th>
              <th>Receipt</th>
              <th className="numeric">Amount (KES)</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {contributions.map((item) => (
              <tr key={item.id}>
                <td>{new Date(item.paidAt).toLocaleString("en-KE", { dateStyle: "medium", timeStyle: "short" })}</td>
                <td>{item.congregantName}</td>
                <td>{purposeLabel(item.purpose)}</td>
                <td><span className="badge">{item.method}</span></td>
                <td>{item.providerTransactionId}</td>
                <td className="numeric">{formatKes(item.amount).replace("KES ", "")}</td>
                <td><span className={`badge ${item.status}`}>{item.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Metric({ label, value, plain = false }: { label: string; value: string; plain?: boolean }) {
  return (
    <article className="metric">
      <span>{label}</span>
      <strong>{plain ? value : formatKes(value)}</strong>
    </article>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <section className="loading-state">
      <Landmark size={22} />
      <span>{label}</span>
    </section>
  );
}
